import { cn } from "@/lib/utils";
import { PhoneMockup } from "./PhoneMockup";
import AppStoreBadge from "../downloadFromAppStore.svg";

export interface AppShowcaseProps {
  title: string;
  description: string;
  phoneImage?: string;
  privacyPolicyUrl: string;
  termsOfServiceUrl: string;
  variant?: "default" | "reversed" | "centered";
  accentColor?: string;
  gradientFrom?: string;
  gradientTo?: string;
  appStoreUrl?: string;
}

export function AppShowcase({
  title,
  description,
  phoneImage,
  privacyPolicyUrl,
  termsOfServiceUrl,
  variant = "default",
  gradientFrom,
  gradientTo,
  appStoreUrl,
}: AppShowcaseProps) {
  if (variant === "centered") {
    return (
      <div className="flex flex-col items-center text-center">
        <PhoneMockup
          image={phoneImage}
          alt={`${title} app screenshot`}
          className="mb-8"
          gradientFrom={gradientFrom}
          gradientTo={gradientTo}
        />
        <div className="max-w-md">
          <h3 className="text-2xl font-medium text-foreground mb-4">{title}</h3>
          <p className="text-muted-foreground leading-relaxed mb-4">
            {description}
          </p>
          {appStoreUrl && (
            <a href={appStoreUrl} target="_blank" rel="noopener noreferrer" className="inline-block mb-4">
              <img src={AppStoreBadge} alt="Download on the App Store" className="h-10" />
            </a>
          )}
          <div className="flex items-center justify-center gap-4 text-sm text-muted-foreground">
            <a
              href={privacyPolicyUrl}
              className="hover:text-foreground transition-colors underline underline-offset-4"
            >
              Privacy Policy
            </a>
            <span className="text-border">|</span>
            <a
              href={termsOfServiceUrl}
              className="hover:text-foreground transition-colors underline underline-offset-4"
            >
              Terms of Service
            </a>
          </div>
        </div>
      </div>
    );
  }

  const isReversed = variant === "reversed";

  return (
    <div
      className={cn(
        "flex flex-col items-center gap-8 md:gap-12",
        "md:flex-row md:items-center",
        isReversed && "md:flex-row-reverse"
      )}
    >
      <div className="flex-shrink-0">
        <PhoneMockup
          image={phoneImage}
          alt={`${title} app screenshot`}
          gradientFrom={gradientFrom}
          gradientTo={gradientTo}
        />
      </div>
      <div
        className={cn(
          "flex-1 text-center md:text-left",
          isReversed && "md:text-right"
        )}
      >
        <h3 className="text-2xl sm:text-3xl font-medium text-foreground mb-4">
          {title}
        </h3>
        <p className={`text-muted-foreground leading-relaxed mb-6 max-w-md mx-auto md:mx-0 ${isReversed ? "!ml-auto": ""}`}>
          {description}
        </p>
        {appStoreUrl && (
          <div className={cn(
            "flex mb-6",
            "justify-center md:justify-start",
            isReversed && "md:justify-end"
          )}>
            <a href={appStoreUrl} target="_blank" rel="noopener noreferrer">
              <img src={AppStoreBadge} alt="Download on the App Store" className="h-10" />
            </a>
          </div>
        )}
        <div
          className={cn(
            "flex items-center gap-4 text-sm text-muted-foreground",
            "justify-center md:justify-start",
            isReversed && "md:justify-end"
          )}
        >
          <a
            href={privacyPolicyUrl}
            className="hover:text-foreground transition-colors underline underline-offset-4"
          >
            Privacy Policy
          </a>
          <span className="text-border">|</span>
          <a
            href={termsOfServiceUrl}
            className="hover:text-foreground transition-colors underline underline-offset-4"
          >
            Terms of Service
          </a>
        </div>
      </div>
    </div>
  );
}
